"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.importConfiguration = importConfiguration;
const shared_1 = require("../../../../backend/features/configurable-lists/shared");
const shared_2 = require("../../../../backend/features/editable-pages/shared");
const shared_3 = require("../../../../backend/features/editable-settings/shared");
const lodash_isequal_1 = __importDefault(require("lodash.isequal"));
function importConfiguration({ t, features, classifiers, file, dryRun, progressCallback, getStorageClient, }) {
    let status = 'importing';
    let total = 0;
    let count = 0;
    let changed = 0;
    let unchanged = 0;
    const cancelRef = { cancel: false };
    const result = {};
    progressCallback({ status, total, count, changed, unchanged });
    let label = '';
    let lastProcessedItemIdentifier;
    (async function () {
        if (features.includes('editable-settings') && file['editable-settings']) {
            total += file['editable-settings'].length;
        }
        if (features.includes('editable-pages') && file['editable-pages']) {
            total += file['editable-pages'].length;
        }
        if (features.includes('configurable-lists') && file['configurable-lists']) {
            total += file['configurable-lists'].length;
        }
        if (features.includes('classifiers') && classifiers && file['classifiers']) {
            for (const { entityName } of classifiers) {
                if (file.classifiers[entityName]) {
                    total += file.classifiers[entityName].length;
                }
            }
        }
        status = 'importing';
        progressCallback({ status, total, count, changed, unchanged });
        if (features.includes('editable-settings') && file['editable-settings']) {
            label = t('configurationTransfer.featuresEnum.editable-settings');
            progressCallback({ status, total, count, label, changed, unchanged });
            result['editable-settings'] = await setAllData({
                storageClient: getStorageClient(shared_3.settingEntityName),
                items: file['editable-settings'].map(({ name, value }) => ({ name, value })),
                dryRun,
                filter: (data) => [{ column: 'name', op: '=', value: data.name }],
                cancelRef,
                onBeforeItemProcessing: (itemIdentifier) => {
                    lastProcessedItemIdentifier = itemIdentifier;
                },
                countCallback: (change) => {
                    if (change) {
                        ++changed;
                    }
                    else {
                        ++unchanged;
                    }
                    progressCallback({ status, total, count: ++count, label, changed, unchanged });
                },
                partialUpdate: true,
            });
            if (cancelRef.cancel) {
                return;
            }
            label = '';
        }
        if (features.includes('editable-pages') && file['editable-pages']) {
            label = t('configurationTransfer.featuresEnum.editable-pages');
            progressCallback({ status, total, count, label, changed, unchanged });
            result['editable-pages'] = await setAllData({
                storageClient: getStorageClient(shared_2.editablePageEntityName),
                items: file['editable-pages'],
                dryRun,
                filter: (data) => [{ column: 'url', op: '=', value: data.url }],
                cancelRef,
                onBeforeItemProcessing: (itemIdentifier) => {
                    lastProcessedItemIdentifier = itemIdentifier;
                },
                countCallback: (change) => {
                    if (change) {
                        ++changed;
                    }
                    else {
                        ++unchanged;
                    }
                    progressCallback({ status, total, count: ++count, label, changed, unchanged });
                },
            });
            if (cancelRef.cancel) {
                return;
            }
            label = '';
        }
        if (features.includes('configurable-lists') && file['configurable-lists']) {
            label = t('configurationTransfer.featuresEnum.configurable-lists');
            progressCallback({ status, total, count, label, changed, unchanged });
            result['configurable-lists'] = await setAllData({
                storageClient: getStorageClient(shared_1.configurableListEntityName),
                items: file['configurable-lists'],
                dryRun,
                filter: (data) => [{ column: 'name', op: '=', value: data.value }],
                cancelRef,
                onBeforeItemProcessing: (itemIdentifier) => {
                    lastProcessedItemIdentifier = itemIdentifier;
                },
                countCallback: (change) => {
                    if (change) {
                        ++changed;
                    }
                    else {
                        ++unchanged;
                    }
                    progressCallback({ status, total, count: ++count, label, changed, unchanged });
                },
            });
            if (cancelRef.cancel) {
                return;
            }
            label = '';
        }
        if (features.includes('classifiers') && classifiers && file['classifiers']) {
            const classifiersResult = {};
            for (const { entityName, label: classifierLabel, onImportMapData } of classifiers) {
                if (file.classifiers[entityName]) {
                    label = t('configurationTransfer.classifier') + ' ' + classifierLabel;
                    progressCallback({ status, total, count, label, changed, unchanged });
                    classifiersResult[entityName] = await setAllData({
                        storageClient: getStorageClient(entityName),
                        items: file.classifiers[entityName],
                        dryRun,
                        filter: (data) => [{ column: 'name', op: '=', value: data.name }],
                        cancelRef,
                        onBeforeItemProcessing: (itemIdentifier) => {
                            lastProcessedItemIdentifier = itemIdentifier;
                        },
                        countCallback: (change) => {
                            if (change) {
                                ++changed;
                            }
                            else {
                                ++unchanged;
                            }
                            progressCallback({ status, total, count: ++count, label, changed, unchanged });
                        },
                        onImportMapData,
                    });
                    if (cancelRef.cancel) {
                        return;
                    }
                    label = '';
                }
            }
            if (Object.keys(classifiersResult).length > 0) {
                result['classifiers'] = classifiersResult;
            }
        }
    })()
        .then(() => {
        status = cancelRef.cancel ? 'canceled' : 'done';
        progressCallback({
            status,
            total,
            count,
            changed,
            unchanged,
            result: status === 'done' ? JSON.stringify(result) : undefined,
        });
    })
        .catch((error) => {
        status = 'error';
        progressCallback({
            status,
            total,
            count,
            errorItemKey: lastProcessedItemIdentifier,
            label,
            error,
            changed,
            unchanged,
        });
    });
    return {
        cancel: () => {
            status = 'canceling';
            progressCallback({ status, total, count, changed, unchanged });
            cancelRef.cancel = true;
        },
    };
}
async function setAllData({ storageClient, items, dryRun, filter, cancelRef, countCallback, onImportMapData, onBeforeItemProcessing, partialUpdate, }) {
    const result = [];
    for (let item of items) {
        const name = typeof item?.name === 'string' ? item?.name : undefined;
        onBeforeItemProcessing?.(name);
        if (onImportMapData) {
            item = await onImportMapData(item);
            if (cancelRef.cancel) {
                return result;
            }
        }
        const id = (await storageClient.list({ filter: filter(item), limit: 1 })).rows[0]?.id;
        if (cancelRef.cancel) {
            return result;
        }
        let changed = false;
        if (id) {
            const data = (await storageClient.get(id)).data;
            if (!(0, lodash_isequal_1.default)(item, data)) {
                if (!dryRun) {
                    await storageClient.post({ action: partialUpdate ? 'partial-update' : 'update', id: id, data: item });
                }
                changed = true;
            }
        }
        else {
            if (!dryRun) {
                await storageClient.post({ action: 'create', data: item });
            }
            changed = true;
        }
        if (cancelRef.cancel) {
            return result;
        }
        if (changed) {
            result.push(item);
        }
        countCallback(changed);
    }
    return result;
}
